import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {Trans} from '@common/i18n/trans';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {useDialogContext} from '@common/ui/overlays/dialog/dialog-context';
import React, {useState} from 'react';
import {ButtonBase} from '@common/ui/buttons/button-base';
import {iconGridStyle} from '@common/ui/icon-picker/icon-grid-style';
import clsx from 'clsx';
import {useFilter} from '@common/i18n/use-filter';
import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {useTrans} from '@common/i18n/use-trans';
import {
  getMaterialIconsList,
  MaterialIconConfig,
} from '@app/editor/utils/get-material-icons-list';
import {Select} from '@common/ui/forms/select/select';
import {Item} from '@common/ui/forms/listbox/item';

type IconVariant = 'filled' | 'outlined' | 'rounded' | 'sharp' | 'two-tone';

export function MaterialIconSelectorDialog() {
  const {trans} = useTrans();
  const {close} = useDialogContext();

  const [icons] = useState<MaterialIconConfig[]>(() => {
    return getMaterialIconsList();
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVariant, setSelectedVariant] = useState<IconVariant>('filled');

  const {contains} = useFilter({
    sensitivity: 'base',
  });

  const filteredIcons = icons.filter(config =>
    contains(config.name, searchQuery),
  );

  const getIconClass = (variant: IconVariant) => {
    switch (variant) {
      case 'filled':
        return 'material-icons';
      case 'outlined':
        return 'material-icons-outlined';
      case 'rounded':
        return 'material-icons-round';
      case 'sharp':
        return 'material-icons-sharp';
      case 'two-tone':
        return 'material-icons-two-tone';
      default:
        return 'material-icons';
    }
  };

  return (
    <Dialog size="2xl">
      <DialogHeader>
        <Trans message="Select a Material Icon" />
      </DialogHeader>
      <DialogBody>
        <div className="mb-20 flex gap-12">
          <TextField
            className="flex-1"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
            }}
            placeholder={trans({message: 'Search icons...'})}
          />
          <Select
            selectionMode="single"
            selectedValue={selectedVariant}
            onSelectionChange={(value: string | number) =>
              setSelectedVariant(value as IconVariant)
            }
            className="min-w-150"
          >
            <Item value="filled">
              <Trans message="Filled" />
            </Item>
            <Item value="outlined">
              <Trans message="Outlined" />
            </Item>
            <Item value="rounded">
              <Trans message="Rounded" />
            </Item>
            <Item value="sharp">
              <Trans message="Sharp" />
            </Item>
            <Item value="two-tone">
              <Trans message="Two Tone" />
            </Item>
          </Select>
        </div>
        <div className={iconGridStyle.grid}>
          {filteredIcons.map(config => {
            const iconClass = getIconClass(selectedVariant);
            return (
              <ButtonBase
                onClick={() => close({class: iconClass, name: config.name})}
                className={clsx(iconGridStyle.button, 'text-3xl')}
                key={config.name}
              >
                <span className={iconClass}>{config.name}</span>
                <span className="mt-16 block whitespace-normal text-xs">
                  {config.name.replace(/_/g, ' ')}
                </span>
              </ButtonBase>
            );
          })}
        </div>
      </DialogBody>
    </Dialog>
  );
}

